import { isAbortError } from './error';
import { retryRequest } from './retry-request';

export interface TurnstileValidatorConfig {
  validationUrl: string;
  secret: string;
}

export class TurnstileValidator {
  #config;

  constructor(config: TurnstileValidatorConfig) {
    this.#config = config;
  }

  async validateResponse({
    response,
    expectedAction,
    expectedHostname,
    signal,
  }: TurnstileValidationOptions): Promise<TurnstileValidationResult> {
    try {
      signal?.throwIfAborted();

      const request = new Request(this.#config.validationUrl, {
        method: 'POST',
        body: JSON.stringify({
          response,
          secret: this.#config.secret,
          idempotency_key: this.createIdempotencyKey(),
        }),
        headers: { 'content-type': 'application/json' },
        signal,
      });

      const res = await this.retry(request.clone(), signal);

      if (!res.ok) {
        return { isValid: false, reason: 'server error', response: res };
      }

      const result = (await res.json()) as CftResponse;

      if (!result.success) {
        return { isValid: false, reason: 'response invalid', result };
      }

      if (expectedAction && result.action !== expectedAction) {
        return {
          isValid: false,
          reason: 'action mismatch',
          expectedAction,
          receivedAction: result.action,
        };
      }

      if (expectedHostname && result.hostname !== expectedHostname) {
        return {
          isValid: false,
          reason: 'hostname mismatch',
          expectedHostname,
          receivedHostname: result.hostname,
        };
      }

      return { isValid: true };
    } catch (error) {
      if (isAbortError(error)) {
        return { isValid: false, isAborted: true, reason: 'aborted', error };
      }

      return { isValid: false, reason: 'response validation failed', error };
    }
  }

  protected createIdempotencyKey() {
    return crypto.randomUUID();
  }

  protected retry(request: Request, signal: AbortSignal | undefined) {
    return retryRequest(() => fetch(request), { signal });
  }
}

export class LazyTurnstileValidator extends TurnstileValidator {
  #getConfig;

  constructor(getConfig: () => Partial<TurnstileValidatorConfig>) {
    super({
      get validationUrl() {
        return getConfig().validationUrl ?? '';
      },
      get secret() {
        return getConfig().secret ?? '';
      },
    });
    this.#getConfig = getConfig;
  }

  override async validateResponse(options: TurnstileValidationOptions) {
    if (this.isConfigMissing()) {
      return { isValid: false, isAborted: true, reason: 'missing config' };
    }

    return super.validateResponse(options);
  }

  protected isConfigMissing() {
    return !this.#getConfig().validationUrl || !this.#getConfig().secret;
  }
}

export interface TurnstileValidationOptions {
  response?: string;
  expectedAction?: string;
  expectedHostname?: string;
  signal?: AbortSignal;
}

export interface TurnstileValidationResult {
  isValid: boolean;
  isAborted?: boolean;
  reason?: string;
  error?: unknown;
  response?: Response;
  result?: CftResponse;
  expectedAction?: string;
  receivedAction?: string;
  expectedHostname?: string;
  receivedHostname?: string;
}

export interface CftSuccessResponse {
  success: true;
  challenge_ts: string;
  hostname: string;
  action?: string;
  cdata?: string;
}

export interface CftFailedResponse {
  success: false;
  'error-codes': string[];
}

export type CftResponse = CftSuccessResponse | CftFailedResponse;
